import React from 'react';
import { isAppAccessBlocked } from '../src/utils/launchGate';

export const HelpPage: React.FC = () => {
  const appBlocked = isAppAccessBlocked();
  return (
    <div className="min-h-screen bg-cv-page text-cv-secondary selection:bg-cv-interactive/20 selection:text-cv-primary font-sans overflow-x-hidden flex flex-col items-center">
      
      {/* Header */}
      <div className="w-full bg-cv-page border-b border-cv-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 group">
            <h1 className="text-xl font-black text-cv-primary uppercase tracking-tighter group-hover:text-cv-gold transition-colors">
              Chronovela <span className="text-cv-disabled font-medium tracking-normal text-sm lowercase ml-2">help center</span>
            </h1>
          </a>
          {appBlocked ? (
            <span className="text-xs font-bold uppercase tracking-widest text-cv-muted cursor-default">
              Coming Soon
            </span>
          ) : (
            <a href="/app" className="text-xs font-bold uppercase tracking-widest text-cv-muted hover:text-cv-primary transition-colors">
              Go to App
            </a>
          )}
        </div>
      </div>

      <div className="max-w-3xl w-full px-6 py-16 space-y-16">
        
        <section className="text-center space-y-4 mb-10">
          <h1 className="text-4xl md:text-5xl font-black text-cv-primary tracking-tight uppercase">How to use Chronovela</h1>
          <p className="text-lg text-cv-muted max-w-xl mx-auto">Your guide to planning, generating, and editing novels.</p>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter border-b border-cv-border pb-2">A. Quick Start</h2>
          <div className="bg-cv-panel/50 border border-cv-border rounded-xl p-6 md:p-8 space-y-4">
            <ol className="list-decimal list-inside space-y-3 text-cv-secondary">
              <li><strong className="text-cv-primary">Start a new book</strong> from the Project Dashboard.</li>
              <li><strong className="text-cv-primary">Fill out the Story Blueprint</strong> with your genre, plot, and characters.</li>
              <li><strong className="text-cv-primary">Review the Chapter Roadmap</strong> to ensure the AI understands the sequence of events.</li>
              <li><strong className="text-cv-primary">Generate a sample chapter</strong> using the Free Preview tier.</li>
              <li><strong className="text-cv-primary">Apply a Book Credit</strong> to unlock full generation.</li>
              <li><strong className="text-cv-primary">Generate chapters</strong> while Chronovela builds Story Memory.</li>
              <li><strong className="text-cv-primary">Review and edit</strong> using the Manuscript Editor.</li>
              <li><strong className="text-cv-primary">Export DOCX or TXT</strong> when your manuscript is ready.</li>
            </ol>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter border-b border-cv-border pb-2">B. What is a Story Blueprint?</h2>
          <p className="leading-relaxed">
            The Story Blueprint is the foundational document that Chronovela uses to understand your book. It stores the story idea, genre, tone, characters, world rules, and your overarching canon direction.
          </p>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter border-b border-cv-border pb-2">C. What is a Chapter Roadmap?</h2>
          <p className="leading-relaxed">
            The Chapter Roadmap is the chapter-by-chapter plan. You can review and edit this plan before generation begins to ensure the AI follows your precise narrative direction.
          </p>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter border-b border-cv-border pb-2">D. What is Story Memory?</h2>
          <p className="leading-relaxed">
            Story Memory is an active ledger that Chronovela maintains while writing. It automatically records important events, character states, and continuity facts to ensure the story remains consistent across volumes.
          </p>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter border-b border-cv-border pb-2">E. What are Book Credits?</h2>
          <p className="leading-relaxed mb-4">Book Credits are applied to projects to unlock chapter generation.</p>
          <ul className="space-y-2 list-disc list-inside">
            <li><strong className="text-cv-primary">Free Preview</strong> allows limited testing (1 sample chapter).</li>
            <li><strong className="text-cv-primary">Standard Manuscript Credit</strong> unlocks up to 20 chapters.</li>
            <li><strong className="text-cv-primary">Large Manuscript Credit</strong> unlocks up to 30 chapters.</li>
            <li><strong className="text-cv-primary">Epic Manuscript Credit</strong> unlocks up to 40 chapters.</li>
            <li><strong className="text-cv-primary">Rewrite Packs</strong> add extra rewrite and polish attempts.</li>
          </ul>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter border-b border-cv-border pb-2">F. How do I edit my manuscript?</h2>
          <p className="leading-relaxed mb-4">The Manuscript Review Workspace provides a clean, safe environment for manual edits:</p>
          <ul className="space-y-2 list-disc list-inside">
            <li>Select a chapter from the sidebar or dropdown.</li>
            <li>Edit text freely in the editor area.</li>
            <li>Click <strong className="text-cv-primary">Save Edits</strong> to persist your changes securely.</li>
            <li>Unsaved changes are safely backed up locally to prevent accidental loss.</li>
          </ul>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter border-b border-cv-border pb-2">G. How do I export?</h2>
          <p className="leading-relaxed">
            Completed paid projects can be exported to DOCX or TXT formats. Chronovela intelligently uses your saved edited text when available, falling back to the original generated text otherwise.
          </p>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter border-b border-cv-border pb-2">H. Troubleshooting</h2>
          <ul className="space-y-4">
            <li className="bg-cv-panel border border-cv-border p-4 rounded-lg">
              <strong className="text-cv-primary block mb-1">If generation pauses:</strong>
              Use the Resume action if available in the dashboard.
            </li>
            <li className="bg-cv-panel border border-cv-border p-4 rounded-lg">
              <strong className="text-cv-primary block mb-1">If a chapter needs changes:</strong>
              Use the Rewrite tool, or enter the Manuscript Review Workspace to edit it manually.
            </li>
            <li className="bg-cv-panel border border-cv-border p-4 rounded-lg">
              <strong className="text-cv-primary block mb-1">If payment succeeds but credits do not appear:</strong>
              Refresh the page. If the issue persists, contact support.
            </li>
            <li className="bg-cv-panel border border-cv-border p-4 rounded-lg">
              <strong className="text-cv-primary block mb-1">If export is locked:</strong>
              Confirm the project has a paid Book Credit applied to it.
            </li>
            <li className="bg-cv-panel border border-cv-border p-4 rounded-lg">
              <strong className="text-cv-primary block mb-1">If mobile layout feels cramped:</strong>
              Desktop is recommended for heavy manuscript editing and roadmap planning.
            </li>
          </ul>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter border-b border-cv-border pb-2">I. Best Practices</h2>
          <ul className="space-y-2 list-disc list-inside">
            <li>Give clear character names.</li>
            <li>Review the roadmap before writing.</li>
            <li>Use director notes for strong direction.</li>
            <li>Edit the final manuscript before publishing.</li>
            <li>Treat AI output as a draft.</li>
          </ul>
        </section>

        <section className="mt-16 pt-8 border-t border-cv-border text-center">
          <p className="text-sm text-cv-muted uppercase tracking-widest font-bold mb-2">Need more help?</p>
          <a href="mailto:service@Chronovela.app" className="text-cv-gold hover:text-cv-interactive transition-colors text-lg font-medium">service@Chronovela.app</a>
        </section>

      </div>

      <footer className="w-full border-t border-cv-border mt-auto py-8 text-center text-cv-disabled text-xs flex flex-wrap justify-center gap-4">
        <a href="/" className="hover:text-cv-muted">Home</a>
        <a href="/terms" className="hover:text-cv-muted">Terms of Service</a>
        <a href="/privacy" className="hover:text-cv-muted">Privacy Policy</a>
        <a href="/refund-policy" className="hover:text-cv-muted">Refund Policy</a>
      </footer>

    </div>
  );
};
